import React, { useState } from 'react';
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { AlertCircle, ArrowRight, ArrowUp, ArrowDown } from 'lucide-react';
import type { GaitSession, StrideData } from '../AdvancedGaitAnalysis';

interface GaitMetricsProps {
  session: GaitSession;
  selectedStride: StrideData | undefined;
  compareWithBaseline: boolean;
}

// Type for metric status
type MetricStatus = 'normal' | 'low' | 'high';

// Define metric object type
interface MetricData {
  name: string;
  value: number;
  baseline: number;
  status: MetricStatus;
}

// Mock baseline stride (this would come from the database in a real application)
const baselineStride: StrideData = {
  id: 'baseline-stride',
  timestamp: Date.now() - 30 * 86400000,
  leftStep: {
    id: 'baseline-stride-left',
    timestamp: Date.now() - 30 * 86400000,
    footSide: 'left',
    pressurePoints: [],
    centerOfPressure: { x: 25, y: 50 },
    peakPressure: 82,
    contactArea: 145,
    duration: 580,
    stepLength: 58,
    propulsionForce: 440
  },
  rightStep: {
    id: 'baseline-stride-right',
    timestamp: Date.now() - 30 * 86400000 + 580,
    footSide: 'right',
    pressurePoints: [],
    centerOfPressure: { x: 65, y: 50 },
    peakPressure: 80,
    contactArea: 142,
    duration: 575,
    stepLength: 57,
    propulsionForce: 435
  },
  strideLength: 115,
  strideTime: 1155,
  symmetryIndex: 95,
  cadence: 52,
  velocity: 100,
  baseOfSupport: 12
};

// Clinical reference ranges for gait parameters (these are hypothetical values)
const referenceRanges = {
  cadence: { min: 90, max: 130 }, // steps per minute
  velocity: { min: 2.6, max: 4.6 }, // ft/second
  strideLength: { min: 100, max: 150 }, // centimeters
  stancePercentage: { min: 58, max: 62 }, // percentage of gait cycle
  stepWidth: { min: 8, max: 16 } // centimeters
};

// Helper function to determine if a value is within reference range
const getStatusForValue = (value: number, min: number, max: number): MetricStatus => {
  if (value < min) return 'low';
  if (value > max) return 'high';
  return 'normal';
};

// Helper function to get a color based on status
const getStatusColor = (status: MetricStatus): string => {
  switch (status) {
    case 'normal': return 'text-green-600';
    case 'low': return 'text-orange-600';
    case 'high': return 'text-red-600';
    default: return 'text-gray-600';
  }
};

// Helper function to get change indicator
const getChangeIndicator = (current: number, baseline: number) => {
  const percentChange = ((current - baseline) / baseline) * 100;
  
  if (Math.abs(percentChange) < 2) return { 
    icon: <ArrowRight className="text-gray-500" size={16} />,
    text: 'No significant change',
    color: 'text-gray-500'
  };
  
  if (percentChange > 0) return {
    icon: <ArrowUp className="text-red-500" size={16} />,
    text: `${percentChange.toFixed(1)}% increase`,
    color: 'text-red-500'
  };
  
  return {
    icon: <ArrowDown className="text-green-500" size={16} />,
    text: `${Math.abs(percentChange).toFixed(1)}% decrease`,
    color: 'text-green-500'
  };
};

const GaitMetrics: React.FC<GaitMetricsProps> = ({
  session,
  selectedStride,
  compareWithBaseline
}) => {
  const [metricType, setMetricType] = useState<'spatial' | 'temporal' | 'kinetic'>('spatial');
  
  if (!selectedStride) {
    return <div className="p-6 text-center text-gray-500">No stride data selected</div>;
  }

  // Calculate derived metrics
  const stanceDuration = selectedStride.leftStep.duration + selectedStride.rightStep.duration;
  const swingDuration = selectedStride.strideTime - stanceDuration;
  const stancePercentage = (stanceDuration / selectedStride.strideTime) * 100;
  const swingPercentage = 100 - stancePercentage;
  const stepWidth = selectedStride.baseOfSupport; // Using baseOfSupport as step width

  // Calculate the same metrics for baseline
  const baselineStanceDuration = baselineStride.leftStep.duration + baselineStride.rightStep.duration;
  const baselineSwingDuration = baselineStride.strideTime - baselineStanceDuration;
  const baselineStancePercentage = (baselineStanceDuration / baselineStride.strideTime) * 100;
  const baselineSwingPercentage = 100 - baselineStancePercentage;
  
  // Create data for phase distribution pie chart
  const phaseData = [
    { name: 'Left Stance', value: selectedStride.leftStep.duration },
    { name: 'Right Stance', value: selectedStride.rightStep.duration },
    { name: 'Swing', value: swingDuration }
  ];
  
  const COLORS = ['#3b82f6', '#f97316', '#a3a3a3'];
  
  // Create temporal metrics data
  const temporalMetrics: MetricData[] = [
    {
      name: 'Cadence (steps/min)',
      value: selectedStride.cadence,
      baseline: baselineStride.cadence,
      status: getStatusForValue(
        selectedStride.cadence, 
        referenceRanges.cadence.min, 
        referenceRanges.cadence.max
      )
    },
    {
      name: 'Stride Time (s)',
      value: selectedStride.strideTime / 1000,
      baseline: baselineStride.strideTime / 1000,
      status: 'normal' // No specific reference range
    },
    {
      name: 'Left Step Time (s)',
      value: selectedStride.leftStep.duration / 1000,
      baseline: baselineStride.leftStep.duration / 1000,
      status: 'normal' // No specific reference range
    },
    {
      name: 'Right Step Time (s)',
      value: selectedStride.rightStep.duration / 1000,
      baseline: baselineStride.rightStep.duration / 1000,
      status: 'normal' // No specific reference range
    },
    {
      name: 'Stance Phase (%)',
      value: stancePercentage,
      baseline: baselineStancePercentage,
      status: getStatusForValue(
        stancePercentage, 
        referenceRanges.stancePercentage.min, 
        referenceRanges.stancePercentage.max
      )
    }
  ];
  
  // Create spatial metrics data
  const spatialMetrics: MetricData[] = [
    {
      name: 'Walking Speed (ft/s)',
      value: selectedStride.velocity * 0.0328,
      baseline: baselineStride.velocity * 0.0328,
      status: getStatusForValue(
        selectedStride.velocity * 0.0328, 
        referenceRanges.velocity.min, 
        referenceRanges.velocity.max
      )
    },
    {
      name: 'Stride Length (cm)',
      value: selectedStride.strideLength,
      baseline: baselineStride.strideLength,
      status: getStatusForValue(
        selectedStride.strideLength, 
        referenceRanges.strideLength.min, 
        referenceRanges.strideLength.max
      )
    },
    {
      name: 'Left Step Length (cm)',
      value: selectedStride.leftStep.stepLength,
      baseline: baselineStride.leftStep.stepLength,
      status: 'normal' // No specific reference range
    },
    {
      name: 'Right Step Length (cm)',
      value: selectedStride.rightStep.stepLength,
      baseline: baselineStride.rightStep.stepLength,
      status: 'normal' // No specific reference range
    },
    {
      name: 'Step Width (cm)',
      value: stepWidth,
      baseline: baselineStride.baseOfSupport,
      status: getStatusForValue(
        stepWidth, 
        referenceRanges.stepWidth.min, 
        referenceRanges.stepWidth.max
      )
    }
  ];
  
  // Create kinetic metrics data
  const kineticMetrics: MetricData[] = [
    {
      name: 'Left Peak Pressure (N/cm²)',
      value: selectedStride.leftStep.peakPressure,
      baseline: baselineStride.leftStep.peakPressure,
      status: 'normal' // No specific reference range
    },
    {
      name: 'Right Peak Pressure (N/cm²)',
      value: selectedStride.rightStep.peakPressure,
      baseline: baselineStride.rightStep.peakPressure,
      status: 'normal' // No specific reference range
    },
    {
      name: 'Left Contact Area (cm²)',
      value: selectedStride.leftStep.contactArea,
      baseline: baselineStride.leftStep.contactArea,
      status: 'normal' // No specific reference range
    },
    {
      name: 'Right Contact Area (cm²)',
      value: selectedStride.rightStep.contactArea,
      baseline: baselineStride.rightStep.contactArea,
      status: 'normal' // No specific reference range
    },
    {
      name: 'Left Propulsion Force (N)',
      value: selectedStride.leftStep.propulsionForce,
      baseline: baselineStride.leftStep.propulsionForce,
      status: 'normal' // No specific reference range
    },
    {
      name: 'Right Propulsion Force (N)',
      value: selectedStride.rightStep.propulsionForce,
      baseline: baselineStride.rightStep.propulsionForce,
      status: 'normal' // No specific reference range
    }
  ];
  
  // Get current metrics based on selected type
  const currentMetrics = metricType === 'temporal' 
    ? temporalMetrics 
    : metricType === 'spatial'
    ? spatialMetrics
    : kineticMetrics;
  
  // Create trend data for the session
  const temporalTrendData = session.strides.map((stride, index) => {
    const stanceDur = stride.leftStep.duration + stride.rightStep.duration;
    
    return {
      strideNumber: index + 1,
      cadence: stride.cadence,
      strideTime: stride.strideTime / 1000,
      leftStepTime: stride.leftStep.duration / 1000,
      rightStepTime: stride.rightStep.duration / 1000,
      stancePhase: (stanceDur / stride.strideTime) * 100,
      isSelected: stride.id === selectedStride.id
    };
  });
  
  const spatialTrendData = session.strides.map((stride, index) => {
    return {
      strideNumber: index + 1,
      walkingSpeed: stride.velocity * 0.0328,
      strideLength: stride.strideLength,
      leftStepLength: stride.leftStep.stepLength,
      rightStepLength: stride.rightStep.stepLength,
      stepWidth: stride.baseOfSupport,
      isSelected: stride.id === selectedStride.id
    };
  });
  
  const kineticTrendData = session.strides.map((stride, index) => {
    return {
      strideNumber: index + 1,
      leftPeakPressure: stride.leftStep.peakPressure,
      rightPeakPressure: stride.rightStep.peakPressure,
      leftContactArea: stride.leftStep.contactArea,
      rightContactArea: stride.rightStep.contactArea,
      leftPropulsionForce: stride.leftStep.propulsionForce,
      rightPropulsionForce: stride.rightStep.propulsionForce,
      isSelected: stride.id === selectedStride.id
    };
  });
  
  const renderMetricTables = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="font-medium">
            {metricType === 'temporal' 
              ? 'Temporal Parameters' 
              : metricType === 'spatial'
              ? 'Spatial Parameters'
              : 'Kinetic Parameters'}
          </h3>
        </div>
        <div className="p-4">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Metric
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Value
                  </th>
                  {compareWithBaseline && (
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Baseline
                    </th>
                  )}
                  {compareWithBaseline && (
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Change
                    </th>
                  )}
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {currentMetrics.map((metric, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {metric.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {typeof metric.value === 'number' ? metric.value.toFixed(2) : metric.value}
                    </td>
                    {compareWithBaseline && (
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {typeof metric.baseline === 'number' ? metric.baseline.toFixed(2) : metric.baseline}
                      </td>
                    )}
                    {compareWithBaseline && (
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center">
                          {getChangeIndicator(metric.value, metric.baseline).icon}
                          <span className={`ml-1 ${getChangeIndicator(metric.value, metric.baseline).color}`}>
                            {getChangeIndicator(metric.value, metric.baseline).text}
                          </span>
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm">
                      <span className={`${getStatusColor(metric.status)}`}>
                        {metric.status.charAt(0).toUpperCase() + metric.status.slice(1)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="font-medium">Gait Cycle Distribution</h3>
        </div>
        <div className="p-4 flex justify-center">
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={phaseData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {phaseData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value) => [`${(Number(value) / selectedStride.strideTime * 100).toFixed(1)}% - ${(Number(value) / 1000).toFixed(2)}s`, 'Duration']}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
  
  const renderTrendCharts = () => {
    const trendData = metricType === 'temporal' 
      ? temporalTrendData 
      : metricType === 'spatial'
      ? spatialTrendData
      : kineticTrendData;
    
    // Select which metrics to show based on type
    const metricsToShow = metricType === 'temporal' 
      ? [
          { key: 'cadence', name: 'Cadence (steps/min)', color: '#3b82f6' },
          { key: 'stancePhase', name: 'Stance Phase (%)', color: '#f97316' }
        ]
      : metricType === 'spatial'
      ? [
          { key: 'walkingSpeed', name: 'Walking Speed (ft/s)', color: '#3b82f6' },
          { key: 'strideLength', name: 'Stride Length (cm)', color: '#f97316' }
        ]
      : [
          { key: 'leftPeakPressure', name: 'Left Peak Pressure', color: '#3b82f6' },
          { key: 'rightPeakPressure', name: 'Right Peak Pressure', color: '#f97316' }
        ];
    
    return (
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b">
          <h3 className="font-medium">Metrics Trend Across Session</h3>
        </div>
        <div className="p-4">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="strideNumber" />
              <YAxis />
              <Tooltip />
              <Legend />
              {metricsToShow.map(metric => (
                <Line
                  key={metric.key}
                  type="monotone"
                  dataKey={metric.key}
                  name={metric.name}
                  stroke={metric.color}
                  strokeWidth={2}
                />
              ))}
              
              {/* Add baseline references if comparing */}
              {compareWithBaseline && metricType === 'temporal' && (
                <Line
                  type="monotone"
                  dataKey={() => baselineStride.cadence}
                  name="Baseline Cadence"
                  stroke="#9ca3af"
                  strokeDasharray="4 4"
                />
              )}
              
              {compareWithBaseline && metricType === 'spatial' && (
                <Line
                  type="monotone"
                  dataKey={() => baselineStride.velocity * 0.0328}
                  name="Baseline Speed"
                  stroke="#9ca3af"
                  strokeDasharray="4 4"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };
  
  const renderStepComparison = () => {
    const leftRightData = metricType === 'temporal' 
      ? [
          { name: 'Step Duration (s)', left: selectedStride.leftStep.duration / 1000, right: selectedStride.rightStep.duration / 1000 }
        ]
      : metricType === 'spatial'
      ? [
          { name: 'Step Length (cm)', left: selectedStride.leftStep.stepLength, right: selectedStride.rightStep.stepLength }
        ]
      : [
          { name: 'Peak Pressure (N/cm²)', left: selectedStride.leftStep.peakPressure, right: selectedStride.rightStep.peakPressure },
          { name: 'Contact Area (cm²)', left: selectedStride.leftStep.contactArea, right: selectedStride.rightStep.contactArea },
          { name: 'Propulsion Force (N)', left: selectedStride.leftStep.propulsionForce, right: selectedStride.rightStep.propulsionForce }
        ];
    
    return (
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b">
          <h3 className="font-medium">Left vs. Right Comparison</h3>
        </div>
        <div className="p-4">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={leftRightData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="left" name="Left Foot" fill="#3b82f6" />
              <Bar dataKey="right" name="Right Foot" fill="#f97316" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };
  
  return (
    <div className="p-6">
      {/* Metric Type Selector */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex justify-between items-center">
          <h3 className="font-medium">Gait Parameter Analysis</h3>
          <div className="flex space-x-2">
            <button
              onClick={() => setMetricType('spatial')}
              className={`px-3 py-1 text-sm rounded ${
                metricType === 'spatial' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Spatial
            </button>
            <button
              onClick={() => setMetricType('temporal')}
              className={`px-3 py-1 text-sm rounded ${
                metricType === 'temporal' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Temporal
            </button>
            <button
              onClick={() => setMetricType('kinetic')}
              className={`px-3 py-1 text-sm rounded ${
                metricType === 'kinetic' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Kinetic
            </button>
          </div>
        </div>
      </div>
      
      {renderMetricTables()}
      {renderTrendCharts()}
      {renderStepComparison()}
    </div>
  );
};

export default GaitMetrics; 